import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { styled } from '@compiled/react';
import { sub, startOfDay } from 'date-fns';
import { fetchQuery, graphql, useRefetchableFragment, useRelayEnvironment } from 'react-relay';
import { token } from '@atlaskit/tokens';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import {
	ContextualAnalyticsData,
	FireUiAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import type {
	main_softwareCodePage_PullRequestsList$data,
	main_softwareCodePage_PullRequestsList$key,
} from '@atlassian/jira-relay/src/__generated__/main_softwareCodePage_PullRequestsList.graphql';
import type { mainSoftwareCodePagePullRequestsListRefetch } from '@atlassian/jira-relay/src/__generated__/mainSoftwareCodePagePullRequestsListRefetch.graphql';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import {
	PULL_REQUESTS_EMPTY_STATE_TEST_ID,
	PULL_REQUESTS_TABLE_TEST_ID,
	PULL_REQUESTS_UPDATED_FROM_NUMBER_OF_DAYS,
	MAX_NUMBER_OF_PULL_REQUESTS_TO_QUERY,
	queryParamsToWatch,
} from '../../../../../common/constants';
import { IsOnScreenObserver } from '../../../../../common/ui/is-visible-observer';
import { useHasQueryParamOnRender } from '../../../../../controllers/hooks';
import {
	useAppliedPullRequestFilters,
	usePullRequestTableOptionsStore,
} from '../../../../../controllers/pull-request-filters';
import EmptyState from './empty-state';
import { FilteredTable } from './filtered-table';
import { FiltersUI } from './filters';

type PullRequestsListProps = {
	pullRequestsListData: main_softwareCodePage_PullRequestsList$key;
};

type ArrElement<ArrType> = ArrType extends readonly (infer ElementType)[] ? ElementType : never;
type PrEdge = NonNullable<
	ArrElement<
		main_softwareCodePage_PullRequestsList$data['jira']['jiraProjectByKey']['devOpsEntityRelationships']['edges']
	>
>;
type PullRequestData = NonNullable<PrEdge['node']['to']['data']>;

export const PullRequestsList = ({ pullRequestsListData }: PullRequestsListProps) => {
	const [appliedFilters] = useAppliedPullRequestFilters();
	const [options] = usePullRequestTableOptionsStore();
	const hasFilterOrSortQueryParamOnRender = useHasQueryParamOnRender(queryParamsToWatch);
	const { cloudId } = useTenantContext();
	const { projectKey = '' } = useProjectContext().data ?? {};
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [hasAllDataLoaded, setHasAllDataLoaded] = useState(false);
	const [hasBeenSeen, setHasBeenSeen] = useState(false);
	const [userRefetchCountdown, setUserRefetchCountdown] = useState(3);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [data, refetch] = useRefetchableFragment<
		mainSoftwareCodePagePullRequestsListRefetch,
		main_softwareCodePage_PullRequestsList$key
	>(
		graphql`
			fragment main_softwareCodePage_PullRequestsList on Query
			@refetchable(queryName: "mainSoftwareCodePagePullRequestsListRefetch")
			@argumentDefinitions(
				cloudId: { type: "ID!" }
				projectKey: { type: "String!" }
				first: { type: "Int" }
				updatedFrom: { type: "DateTime!" }
				sort: {
					type: "AriGraphRelationshipsSort"
					defaultValue: { lastUpdatedSortDirection: DESC }
				}
			) {
				jira @required(action: THROW) {
					jiraProjectByKey(cloudId: $cloudId, key: $projectKey) @required(action: THROW) {
						devOpsEntityRelationships(
							type: "project-associated-pr"
							filter: { updatedFrom: $updatedFrom }
							first: $first
							sort: $sort
						) @required(action: THROW) {
							edges @required(action: THROW) {
								node @required(action: THROW) {
									to @required(action: THROW) {
										data {
											... on DevOpsPullRequestDetails {
												status
												repositoryName
												...main_softwareCodePage_FiltersUI
												...main_softwareCodePage_FilteredTable
												authorId @optIn(to: "DevOpsPullRequestInCode")
												author {
													__typename
												}
												reviewers {
													userId @optIn(to: "DevOpsPullRequestInCode")
													user {
														__typename
													}
												}
											}
										}
									}
								}
							}
							pageInfo @required(action: THROW) {
								hasNextPage @required(action: THROW)
							}
						}
					}
				}
			}
		`,
		pullRequestsListData,
	);

	const relayEnvironment = useRelayEnvironment();
	useEffect(() => {
		if (
			data.jira.jiraProjectByKey.devOpsEntityRelationships.pageInfo.hasNextPage &&
			!hasFilterOrSortQueryParamOnRender
		) {
			const variables = Object.freeze({
				cloudId,
				projectKey,
				first: MAX_NUMBER_OF_PULL_REQUESTS_TO_QUERY,
				updatedFrom: startOfDay(
					sub(new Date(), {
						days: PULL_REQUESTS_UPDATED_FROM_NUMBER_OF_DAYS,
					}),
				).toISOString(),
			});
			fetchQuery(
				relayEnvironment,
				graphql`
					query mainSoftwareCodePagePullRequestsListRefetchQuery(
						$cloudId: ID!
						$projectKey: String!
						$first: Int!
						$updatedFrom: DateTime!
					) {
						...main_softwareCodePage_PullRequestsList
							@arguments(
								cloudId: $cloudId
								projectKey: $projectKey
								first: $first
								updatedFrom: $updatedFrom
							)
					}
				`,
				variables,
			).subscribe({
				complete: () => {
					// *After* the query has been fetched, we call
					// refetch again to re-render with the updated data.
					// At this point the data for the query should
					// be cached, so we use the 'store-only'
					// fetchPolicy to avoid suspending.
					refetch(variables, { fetchPolicy: 'store-only' });
					setHasAllDataLoaded(true);
				},
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				error: () => {},
			});
		} else {
			setHasAllDataLoaded(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [relayEnvironment]);

	const prList: PullRequestData[] = useMemo(
		() =>
			data.jira.jiraProjectByKey.devOpsEntityRelationships.edges.flatMap((edge) =>
				edge?.node.to.data ? [edge.node.to.data] : [],
			),
		[data.jira.jiraProjectByKey.devOpsEntityRelationships.edges],
	);
	useEffect(() => {
		if (hasAllDataLoaded) {
			const unHydratedUsers = prList.reduce(
				(users, pr) => {
					if (pr?.authorId) {
						if (pr?.author === null) {
							users.authorsFailed.add(pr.authorId);
						} else {
							users.authorsSuccess.add(pr.authorId);
						}
						users.totalUsers.add(pr.authorId);
					}
					pr?.reviewers?.forEach((reviewer) => {
						if (reviewer?.userId) {
							if (reviewer?.user === null) {
								users.reviewersFailed.add(reviewer.userId);
							} else {
								users.reviewersSuccess.add(reviewer.userId);
							}
							users.totalUsers.add(reviewer.userId);
						}
					});
					return users;
				},
				{
					authorsSuccess: new Set(),
					reviewersSuccess: new Set(),
					authorsFailed: new Set(),
					reviewersFailed: new Set(),
					totalUsers: new Set(),
				},
			);

			if (
				(unHydratedUsers.authorsFailed.size || unHydratedUsers.reviewersFailed.size) &&
				userRefetchCountdown > 0
			) {
				const analyticsEvent = createAnalyticsEvent({});
				fireErrorAnalytics({
					event: analyticsEvent,
					meta: {
						teamName: 'fusion-isotopes',
						packageName: 'jiraSoftwareCodePage',
						id: 'pullRequestListUserHydration',
					},
					attributes: {
						authorsFailed: unHydratedUsers.authorsFailed.size,
						reviewersFailed: unHydratedUsers.reviewersFailed.size,
						authorsSuccess: unHydratedUsers.authorsSuccess.size,
						reviewersSuccess: unHydratedUsers.reviewersSuccess.size,
						totalUsers: unHydratedUsers.totalUsers.size,
					},
					sendToPrivacyUnsafeSplunk: true,
				});

				const variables = Object.freeze({
					cloudId,
					projectKey,
					first: MAX_NUMBER_OF_PULL_REQUESTS_TO_QUERY,
					updatedFrom: startOfDay(
						sub(new Date(), {
							days: PULL_REQUESTS_UPDATED_FROM_NUMBER_OF_DAYS,
						}),
					).toISOString(),
				});
				fetchQuery(
					relayEnvironment,
					graphql`
						query mainSoftwareCodePagePullRequestsListUserRefetchQuery(
							$cloudId: ID!
							$projectKey: String!
							$first: Int!
							$updatedFrom: DateTime!
						) {
							...main_softwareCodePage_PullRequestsList
								@arguments(
									cloudId: $cloudId
									projectKey: $projectKey
									first: $first
									updatedFrom: $updatedFrom
								)
						}
					`,
					variables,
				).subscribe({
					complete: () => {
						// *After* the query has been fetched, we call
						// refetch again to re-render with the updated data.
						// At this point the data for the query should
						// be cached, so we use the 'store-only'
						// fetchPolicy to avoid suspending.

						refetch(variables, { fetchPolicy: 'store-only' });
						setUserRefetchCountdown(userRefetchCountdown - 1);
					},
					// eslint-disable-next-line @typescript-eslint/no-empty-function
					error: () => {},
				});
			} else {
				setUserRefetchCountdown(0);
			}
		}
	}, [
		cloudId,
		createAnalyticsEvent,
		hasAllDataLoaded,
		projectKey,
		refetch,
		relayEnvironment,
		prList,
		userRefetchCountdown,
	]);

	const onVisibilityChange = useCallback(
		(isVisible: boolean) => {
			if (!isVisible && timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
			if (isVisible && !hasBeenSeen) {
				timeoutRef.current = setTimeout(() => {
					fireUIAnalytics(
						createAnalyticsEvent({
							action: 'viewed',
							actionSubject: 'pullRequestListInViewport',
						}),
					);

					setHasBeenSeen(true);
				}, 3000);
			}
		},
		[createAnalyticsEvent, hasBeenSeen],
	);

	const pullRequestCountByStatus = useMemo(
		() =>
			Object.fromEntries(
				prList
					.map((pr) => pr.status)
					.reduce((acc, e) => acc.set(e, (acc.get(e) || 0) + 1), new Map()),
			),
		[prList],
	);

	const repoCountForPullRequests = useMemo(
		() =>
			prList.map((pr) => pr.repositoryName).reduce((acc, repo) => acc.add(repo), new Set()).size,
		[prList],
	);

	return (
		<>
			<ContextualAnalyticsData
				attributes={{
					pullRequestCount: prList.length,
					pullRequestCountByStatus,
				}}
			>
				<FireUiAnalytics
					action="viewed"
					actionSubject="pullRequestList"
					attributes={{
						searchApplied: appliedFilters.titleFilter,
						filtersAppliedFromQueryParam: hasFilterOrSortQueryParamOnRender,
						repoCountForPullRequests,
						repositoryFilters: appliedFilters.repositoryFilter,
						statusFilters: appliedFilters.statusFilter,
						authorFilters: appliedFilters.authorsFilter,
						reviewerFilters: appliedFilters.reviewersFilter,
						sortKey: options.sortKey,
						sortDirection: options.sortOrder,
					}}
				/>
				{prList && prList.length > 0 ? (
					<IsOnScreenObserver threshold={0.7} onVisibilityChange={onVisibilityChange}>
						<FiltersUI disabled={!hasAllDataLoaded} filtersUiData={prList} />
						<TableContainer data-testid={PULL_REQUESTS_TABLE_TEST_ID}>
							<FilteredTable filteredTableData={prList} isLoading={!hasAllDataLoaded} />
						</TableContainer>
					</IsOnScreenObserver>
				) : (
					<EmptyState data-testid={PULL_REQUESTS_EMPTY_STATE_TEST_ID} />
				)}
			</ContextualAnalyticsData>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableContainer = styled.div({
	margin: `${token('space.050', '4px')} 0 0 0`,
});
